
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  isChapterScreen: boolean;
}

// Create context with a default value
const ThemeContext = createContext<ThemeContextType>({
  theme: 'light',
  toggleTheme: () => {},
  isChapterScreen: false
});

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Initialize theme from localStorage or default to 'light'
  const [theme, setTheme] = useState<Theme>(() => {
    const savedTheme = localStorage.getItem('app-theme');
    return (savedTheme as Theme) || 'light';
  });
  
  // Use location to check if we're on a chapter screen
  const location = useLocation();
  const isChapterScreen = location.pathname.includes('/chapter/');

  // Update localStorage and document class when theme changes
  useEffect(() => {
    localStorage.setItem('app-theme', theme);
    
    // Only apply theme classes if not on chapter screen
    if (!isChapterScreen) {
      if (theme === 'dark') {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    } else {
      // Always remove dark class on chapter screens
      document.documentElement.classList.remove('dark');
    }
  }, [theme, isChapterScreen, location]);

  // When navigating to/from chapter screens
  useEffect(() => {
    if (isChapterScreen) {
      // Remove dark class when entering chapter screen
      document.documentElement.classList.remove('dark');
    } else if (theme === 'dark') {
      // Re-apply dark class when leaving chapter screen if theme is dark
      document.documentElement.classList.add('dark');
    }
  }, [isChapterScreen, location, theme]);

  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme === 'light' ? 'dark' : 'light');
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, isChapterScreen }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
